
import React, { useState } from 'react'

import { Button,Col, Divider, FlexboxGrid, Panel, Row, Tag, Timeline } from 'rsuite'
import OrderCost from './common/OrderCost'
import CustomerCard from './payment/CustomerCard'
import RefundOrderPositions from './refund/RefundOrderPositions'

import { DownloadButton } from '@components/common/DownloadButton'

import { RefundStateContextProvider } from './refund/RefundStateContext'
import OrderCashbackControls from './cashback/OrderCashbackControls'

import { PAYMENT_METHODS } from '@@partner:config/payment-types'
import { COMPLETED_STATUSES } from '@@partner:config/order-status-codes'

import './ViewOrder.scss'
import CustomerFeedback from './feedback/CustomerFeedback'
import { formatOrderPaymentStatus } from '@@partner:helpers/formatters/payment'
import { translateErrorCodeToMessage } from '@@partner:helpers/yookassa'
import { formatDate, formatPrice, isGeoPointValid } from '@@partner:helpers/util'
import { getDeliveryClaimStatusLabel, getDeliveryClaimTypeLabel } from '@@partner:helpers/deliveryClaim'
import { formatOrderReceiptsStatus, formatOrderReceiptsType } from '@@partner:helpers/formatters/receipt'
import { COMPANY_INN } from '@@partner:config/brand'
import OrderOnMap from './map/OrderOnMap'
import EditDeliverySettings from './viewOrderForm/EditDeliverySettings'
import EditAddressAndCoordinates from './viewOrderForm/EditAddressAndCoordinates'
import { ORDER, PAYMENT, RECEIPT } from '@hive/status'
import { STATUS_PAYMENT_RESERVED } from '@@partner:config/order-payment-status-codes'
import { useConfirmPayment } from '@@partner:graphql/mutation/order/confirmPayment.gql'
import { GQL_ID } from '@@partner:types:graphql'
import { notifyOk } from '@@partner:helpers/notification'
import { useFetchSingleOrder } from '@@partner:graphql/query/order/singleOrder.gql'
import { OrderInfo, OrderReceipt } from '@@partner:types:app'

export interface ViewOrderProps {
  order: OrderInfo
}

export default function ViewOrder ({ order }: ViewOrderProps) {
  const [ changeAddress, setChangeAddress ] = useState(false)
  const [ editAddress, setEditAddress ] = useState(false)

  const { data, refetch } = useFetchSingleOrder(order.id)

  const isCashbackControlsDisabled =
    !COMPLETED_STATUSES.includes(order.status) ||
    (!!order.totalCashback && order.totalCashback > 0)


  const [ confirmPayment, { error } ] = useConfirmPayment()

  const handleConfirmPayment = async (payment_id: GQL_ID) => {
    if (window.confirm(
      `Подтвердить оплату по счету по заказу ${order.id}?`
    )) {
      const res = await confirmPayment({
        variables: {
          order_id: order.id,
          payment_id
        }
      })

      if (res.data?.confirmPayment.ok) {
        notifyOk('Заказ оплачен')
        refetch()
      }
    }
  }

  const address = [ order.address, order.address_appartments ? `кв. ${order.address_appartments}` : null, order.address_porch ? `подъезд ${order.address_porch}` : null, order.address_floor ? `этаж ${order.address_floor}` : null, order.address_intercom ? `домофон ${order.address_intercom}` : null ].filter(Boolean).join(', ')
  const adressString = address.length > 0 ? address : 'Адрес не указан'
  
  const makeReceiptUrl = (receipt: OrderReceipt) => {
    const receiptMask = import.meta.env.VITE_APP_RECEIPT_MASK as string
    if (!receiptMask || receipt.status !== RECEIPT.DONE || receipt.ecr_registration_number == null || receipt.fn_number == null || receipt.fiscal_document_number == null || receipt.fiscal_document_attribute == null) {
      return null
    }
    return receiptMask.replace('{inn}', COMPANY_INN)
      .replace('{ecr_registration_number}', receipt.ecr_registration_number ?? '')
      .replace('{fn_number}', receipt.fn_number ?? '')
      .replace('{fiscal_document_number}', receipt.fiscal_document_number ?? '')
      .replace('{fiscal_document_attribute}', receipt.fiscal_document_attribute ?? '')
  }

  return (
    <div className="ViewOrder">
      <Row className="mb-6">
        <Col xs={24} sm={24} md={10}>
          <Panel 
            shaded
            header="Информация о покупателе"
            className='ViewOrder__Info__Row'
          >
            <CustomerCard customer_id={order.assignedCustomerId} />
          </Panel>
          <Panel
            shaded
            header="Итого"
            className='ViewOrder__Info__Row'
          >
            <OrderCost order={order} readOnly />
          </Panel>
          <Panel
            shaded
            header='Адрес доставки'
          >
            {editAddress && ![ ...ORDER.DELIVERY_COMPLETE_STATUSES, ORDER.REFUNDED, ORDER.CANCELED ].includes(order.status) ? (
              <EditAddressAndCoordinates
                order={order}
                onCancel={() => setEditAddress(false)}
                onSave={() => {
                  setEditAddress(false)
                  refetch()
                }}
              />
            ) : (
              <>
                <div className="ViewOrder__Info__Row">
                  <strong>Адрес:</strong> { adressString }
                  
                </div>
                { isGeoPointValid(order.customerGeoPoint) && <OrderOnMap order={order} /> }
                {![ ...ORDER.DELIVERY_COMPLETE_STATUSES, ORDER.REFUNDED, ORDER.CANCELED ].includes(order.status) && (
                  <Button
                    onClick={() => setEditAddress(true)}
                    appearance='ghost'
                    className='mt-3'
                  >
                    Изменить адрес и координаты
                  </Button>
                )}
              </>
            )}
          </Panel>
          <Panel
            shaded
            header='Настройка доставки'
            className='ViewOrder__Info__Row'
          >
            { changeAddress && ![ ...ORDER.DELIVERY_COMPLETE_STATUSES, ORDER.REFUNDED, ORDER.CANCELED ].includes(order.status) ? (
              <EditDeliverySettings 
                order={order}
                onCancel={() => setChangeAddress(false)}
                onSave={() => setChangeAddress(false)}
              />
            ) : (
              <>
                
                { order.deliveryType === 'slot' || order.deliveryType === 'pickup' ? (
                  <div className="ViewOrder__Info__Row">
                    <strong>Слот доставки:</strong>{' '}
                    <Tag color="blue">
                      {order.deliverySlot?.slotDate ?? ''}{' '}
                      {order.deliverySlot?.slotTime ?? ''}
                    </Tag>
                  </div>
                ) : (
                  <div className="ViewOrder__Info__Row">
                    <strong>Экспресс:</strong>{' '}
                    <Tag color="blue">
                      { formatDate(new Date(order.placedAt + ((order?.deliveryZone?.expressDeliveryMaxShippingTime ?? 0) * 60 * 1000)))}
                    </Tag>
                  </div>
                )}
                <div className="ViewOrder__Info__Row">
                  <strong>Магазин:</strong> {order.shop?.name}{' '}
                  <small>({order.shop?.address})</small>
                </div>
                { order.comment && (
                  <div className="ViewOrder__Info__Row">
                    <strong>Комментарий к заказу:</strong>
                    <div>{order.comment}</div>
                  </div>
                )}
                { order.customerCourierMessage && (
                  <div className="ViewOrder__Info__Row">
                    <strong>Комментарий курьеру:</strong>
                    <div>{order.customerCourierMessage}</div>
                  </div>
                )}
              </>
            )}
            { !changeAddress && ![ ...ORDER.DELIVERY_COMPLETE_STATUSES, ORDER.REFUNDED, ORDER.CANCELED ].includes(order.status) && (
              <Button
                onClick={() => setChangeAddress(!changeAddress)}
                appearance='ghost'
                className='mt-5'
              >
                Изменить
              </Button>
            )}
          </Panel>
         
          <Panel
            shaded
            header='Оплата'
            className='ViewOrder__Info__Row'
          >
            <Panel
              className='ViewOrder__Info__Row'
              header='Способ оплаты'
              bordered
            >
              {order.paymentMethod && <div>
                {order.paymentMethod?.card && <div><p><strong>Платёжная система:</strong> {order.paymentMethod.card}</p></div>}
                {order.paymentMethod?.last && <div><p><strong>Последние 4 цифры:</strong> {order.paymentMethod.last}</p></div>}
                {order.paymentMethod.pluginId && <div><p><strong>Платежный метод</strong> <Tag color='blue'>{PAYMENT_METHODS?.[order.paymentMethod.pluginId]?.description ?? `Неизвестный платежный метод ${order.paymentMethod.pluginId}`}</Tag></p></div>}
                {order.paymentMethod?.pluginId && <div className={'my-5'}><p><strong>Плагин Hive:</strong> <Tag color='blue'>{order.paymentMethod.pluginId}</Tag></p></div>}
              </div>}
            </Panel>
            { order.payments.filter(payment => payment.status !== STATUS_PAYMENT_RESERVED).map(payment => (
              <Panel
                key={`payment:${payment.id}`}
                className='ViewOrder__Info__Row'
                bordered
              >
                <FlexboxGrid 
                  justify='space-between'
                  align='middle'
                >
                  <FlexboxGrid.Item>
                    <div>Платеж #{ payment.id }</div>
                    { payment.cancel_reason && payment.status !== PAYMENT.COMPLETE ? (
                      <div>{ translateErrorCodeToMessage(payment.cancel_reason) }</div>
                    ) : <div></div>}
                    <Tag>
                      {PAYMENT_METHODS[order.payment_type]?.description ??
                        order.payment_type}
                    </Tag>
                    <div>{ formatPrice(payment.sum) }</div>
                  </FlexboxGrid.Item>
                  <FlexboxGrid.Item>
                    <div>
                      { formatDate(new Date(payment.created_at)) }
                    </div>
                    <Tag 
                      color={formatOrderPaymentStatus(payment.status).color}
                    >
                      { formatOrderPaymentStatus(payment?.status).label }
                    </Tag>
                  </FlexboxGrid.Item>
                </FlexboxGrid>
                
                { order.payment_type === 'corp_invoice' && payment.status === 'pending' && (
                  <Button onClick={() => handleConfirmPayment(payment.payment_id)}>
                    Подтвердить
                  </Button>
                )}
              </Panel>
            ))}
            
          </Panel>
          { order.paymentOrderFile.url && (
            <Panel
              shaded
              className='ViewOrder__Info__Row'
              header='Платежное поручение'
            >
              <DownloadButton
                fileName={`${order.id}:paymentOrderFile`}
                url={order.paymentOrderFile.url}
              />
            </Panel>
          )}
          { order.deliveryClaims && order.deliveryClaims.length > 0 ? (
            <Panel
              shaded
              header='Доставка'
              className='ViewOrder__Info__Row'
            >
              { order.deliveryClaims.map(claim => (
                <Panel bordered key={`claim:${claim.id}`}>
                  <FlexboxGrid align='middle' justify='space-between'>
                    <FlexboxGrid.Item>
                      <div>Заявка #{ claim.id }</div>
                      <div>{ getDeliveryClaimTypeLabel(claim.deliveryProvider) }</div>
                    </FlexboxGrid.Item>
                    <FlexboxGrid.Item>
                      <div>{ formatDate(new Date(claim.updated_at)) }</div>
                      <Tag color={getDeliveryClaimStatusLabel(claim.status).color}>{ getDeliveryClaimStatusLabel(claim.status).label }</Tag>
                    </FlexboxGrid.Item>
                  </FlexboxGrid>
                </Panel>
              ))}
            </Panel>
          ) : (
            <div></div>
          )}

          {/* Информация о сборщике */}
          { order.assembler && (
            <Panel
              shaded
              header='Сборщик'
              className='ViewOrder__Info__Row'
            >
              <div className="ViewOrder__Info__Row">
                <strong>Имя:</strong> {order.assembler.first_name} {order.assembler.last_name}
              </div>
              { order.assembler.phone && (
                <div className="ViewOrder__Info__Row">
                  <strong>Телефон:</strong>{' '}
                  <a href={`tel:${order.assembler.phone}`}>{order.assembler.phone}</a>
                </div>
              )}
              { order.assemblingStartedAt && (
                <div className="ViewOrder__Info__Row">
                  <strong>Начал сборку:</strong> {formatDate(new Date(order.assemblingStartedAt))}
                </div>
              )}
              { order.assemblingFinishedAt && (
                <div className="ViewOrder__Info__Row">
                  <strong>Завершил сборку:</strong> {formatDate(new Date(order.assemblingFinishedAt))}
                </div>
              )}
            </Panel>
          )}

          {/* Информация о курьере */}
          { order.courier && (
            <Panel
              shaded
              header='Курьер'
              className='ViewOrder__Info__Row'
            >
              <div className="ViewOrder__Info__Row">
                <strong>Имя:</strong> {order.courier.first_name} {order.courier.last_name}
              </div>
              { order.courier.phone && (
                <div className="ViewOrder__Info__Row">
                  <strong>Телефон:</strong>{' '}
                  <a href={`tel:${order.courier.phone}`}>{order.courier.phone}</a>
                </div>
              )}
              { order.pickedByCourierAt && (
                <div className="ViewOrder__Info__Row">
                  <strong>Забрал заказ:</strong> {formatDate(new Date(order.pickedByCourierAt))}
                </div>
              )}
              { order.deliveryStartedAt && (
                <div className="ViewOrder__Info__Row">
                  <strong>Начал доставку:</strong> {formatDate(new Date(order.deliveryStartedAt))}
                </div>
              )}
              { order.deliveryFinishedAt && (
                <div className="ViewOrder__Info__Row">
                  <strong>Доставил:</strong> {formatDate(new Date(order.deliveryFinishedAt))}
                </div>
              )}
            </Panel>
          )}
          { order.receipts && order.receipts.length > 0 ? (
            <Panel
              shaded
              header='Чеки'
              className='ViewOrder__Info__Row'
            >
              { order.receipts.map(receipt => {
                const receiptUrl = makeReceiptUrl(receipt)
                return (
                  <Panel
                    key={`payment:${receipt.id}`}
                    className='ViewOrder__Info__Row'
                    bordered
                  >
                    <FlexboxGrid 
                      justify='space-between'
                      align='middle'
                    >
                      <FlexboxGrid.Item>
                        <div>Чек #{ receipt.id }</div>
                        <Tag color={formatOrderReceiptsType(
                          receipt.type
                        ).color}>{formatOrderReceiptsType(receipt.type).label}</Tag>
                        <div>
                          <Tag color="violet">{formatPrice(receipt.total ?? 0)}</Tag>
                        </div>
                      </FlexboxGrid.Item>
                      <FlexboxGrid.Item>
                        <div>{formatDate(new Date(receipt?.receipt_created_at))}</div>
                        <Tag className='u-status-badge' color={formatOrderReceiptsStatus(receipt.status)?.color}>{formatOrderReceiptsStatus(receipt.status).label}</Tag>
                      </FlexboxGrid.Item>
                    </FlexboxGrid>
                    { receipt.ofd_receipt_url || receiptUrl && (
                      <Button
                        size="md"
                        as={'a'}
                        href={receipt.ofd_receipt_url || receiptUrl}
                        target="_blank"
                      >
                        Открыть
                      </Button>
                    )}
                  </Panel>
                )
              })}
            </Panel>
          ) : (
            <div></div>
          )}
          <Panel
            shaded
          >
            <Timeline>
              <Timeline.Item>Размещен: { formatDate(new Date(order.placedAt)) }</Timeline.Item>
              { !!order.assemblingStartedAt && <Timeline.Item>Сборка начата: { formatDate(new Date(order.assemblingStartedAt)) }</Timeline.Item>}
              { !!order.assemblingFinishedAt && <Timeline.Item>Сборка завершена: { formatDate(new Date(order.assemblingFinishedAt)) } </Timeline.Item>}
              { !!order.pickedByCourierAt && <Timeline.Item>Курьер забрал: { formatDate(new Date(order.pickedByCourierAt)) } </Timeline.Item>}
              { !!order.deliveryStartedAt && <Timeline.Item>Доставка начата: { formatDate(new Date(order.deliveryStartedAt)) } </Timeline.Item>}
              { !!order.deliveryFinishedAt && <Timeline.Item>Доставлен: { formatDate(new Date(order.deliveryFinishedAt)) } </Timeline.Item>}
            </Timeline>
          </Panel>

          { !!order.feedbacks && order.feedbacks.length > 0 && (
            <Panel bordered header="Отзыв">
              {order.feedbacks.map((feedback) => (
                <CustomerFeedback
                  feedback={feedback}
                  key={`feedback:${feedback.id}`}
                />
              ))}
            </Panel>
          )}
        </Col>
        <Col xs={24} sm={24} md={14}>
          <Panel bordered header="Позиции в заказе">
            <RefundStateContextProvider initial={order.customerProducts ?? []}>
              <RefundOrderPositions order={order} />
              <OrderCost order={order} readOnly />
            </RefundStateContextProvider>

            {/* <div className="text-right my-2">
              <OrderCashbackControls
                order={order}
                disabled={isCashbackControlsDisabled}
              />
            </div> */}
          </Panel>
        </Col>
      </Row>
    </div>
  )
}
